import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { environment } from '../../environment/environment';
import { InvoiceAddressBackendModel } from '../models/address.model';
import {
  CouponBackendModel,
  CouponCodeBackendModel,
  CouponCodeModel,
  CouponModel,
  CouponUsageLogBackendModel,
  CouponUsageLogModel,
} from '../models/coupon.model';

@Injectable({
  providedIn: 'root',
})
export class CouponService {
  constructor(private http: HttpClient) { }

  public getAllSellableCoupons(
    onlyActive: boolean = true
  ): Observable<CouponModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getAllCoupons');
    formData.append('only_active', onlyActive ? '1' : '0');

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: CouponBackendModel[];
      }>(environment.authority_short + 'coupons.php', formData)
      .pipe(
        map(response =>
          response?.data.map(coupon => this.parseBackendModel(coupon))
        ),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  public getPromoCodes(): Observable<CouponCodeModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getAllCouponCodesByType');
    formData.append('type', 'promo');

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: CouponCodeBackendModel[];
      }>(environment.authority_short + 'coupon_codes.php', formData)
      .pipe(
        map(response =>
          response?.data.map(couponCode => this.parseCouponCodeBackendModel(couponCode))
        ),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  public getCouponUsageLogs(): Observable<CouponUsageLogModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getAllCouponUsageLogs');

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: CouponUsageLogBackendModel[];
      }>(environment.authority_short + 'coupon_usage_log.php', formData)
      .pipe(
        map(response =>
          response?.data.map(couponUsageLog =>
            this.parseCouponUsageLog(couponUsageLog)
          )
        ),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  public createCoupon(coupon: CouponModel): Observable<any> {
    const formData = new FormData();
    formData.append('mode', 'createCoupon');
    formData.append('title', coupon.title);
    coupon.description && formData.append('description', coupon.description);
    formData.append('payed_amount', coupon.payedAmount.toString());
    formData.append('received_amount', coupon.receivedAmount.toString());
    formData.append('coupon_type', coupon.couponType);

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'coupons.php', formData)
      .pipe(
        map(response => response),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  public enableCoupon(couponId: number): Observable<any> {
    const formData = new FormData();
    formData.append('mode', 'enableCoupon');
    formData.append('id', couponId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'coupons.php', formData)
      .pipe(
        map(response => response),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  public disableCoupon(couponId: number): Observable<any> {
    const formData = new FormData();
    formData.append('mode', 'disableCoupon');
    formData.append('id', couponId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'coupons.php', formData)
      .pipe(
        map(response => response),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  public deleteCoupon(couponId: number): Observable<any> {
    const formData = new FormData();
    formData.append('mode', 'deleteCoupon');
    formData.append('id', couponId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'coupons.php', formData)
      .pipe(
        map(response => response),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  public createCouponCode(couponCode: CouponCodeModel): Observable<any> {
    const formData = new FormData();
    formData.append('mode', 'createCouponCode');
    formData.append('title', couponCode.title);
    couponCode.description &&
      formData.append('description', couponCode.description);
    formData.append('discount_type', couponCode.discountType);
    formData.append('value', couponCode.value.toString());
    formData.append('code', couponCode.code);

    if (couponCode.expiresAt) {
      formData.append('expires_at', couponCode.expiresAt.toString());
    }

    if (couponCode.maxUses) {
      formData.append('max_uses', couponCode.maxUses.toString());
    }

    if (couponCode.recipient) {
      formData.append('recipient', couponCode.recipient);
    }

    if (couponCode.allowedSeminarIds && couponCode.allowedSeminarIds.length > 0) {
      couponCode.allowedSeminarIds.forEach(id => {
        formData.append('allowed_seminar_ids[]', id.toString());
      });
    }

    if (couponCode.seminarInverted) {
      formData.append('seminar_inverted', '1');
    }

    if (couponCode.allowedConditionTypes && couponCode.allowedConditionTypes.length > 0) {
      couponCode.allowedConditionTypes.forEach(type => {
        formData.append('allowed_condition_types[]', type);
      });
    }

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'coupon_codes.php', formData)
      .pipe(
        map(response => response),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  public enableCouponCode(couponCodeId: number): Observable<any> {
    const formData = new FormData();
    formData.append('mode', 'enableCouponCode');
    formData.append('id', couponCodeId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'coupon_codes.php', formData)
      .pipe(
        map(response => response),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  public disableCouponCode(couponCodeId: number): Observable<any> {
    const formData = new FormData();
    formData.append('mode', 'disableCouponCode');
    formData.append('id', couponCodeId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'coupon_codes.php', formData)
      .pipe(
        map(response => response),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  public deleteCouponCode(couponCodeId: number): Observable<any> {
    const formData = new FormData();
    formData.append('mode', 'deleteCouponCode');
    formData.append('id', couponCodeId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'coupon_codes.php', formData)
      .pipe(
        map(response => response),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  public getCouponCode(couponCodeId: number): Observable<CouponCodeModel> {
    const formData = new FormData();
    formData.append('mode', 'getCouponCode');
    formData.append('id', couponCodeId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: CouponCodeBackendModel;
      }>(environment.authority_short + 'coupon_codes.php', formData)
      .pipe(
        map(response => this.parseCouponCodeBackendModel(response.data)),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  public updateCouponCode(couponCode: CouponCodeModel): Observable<any> {
    const formData = new FormData();
    formData.append('mode', 'updateCouponCode');
    formData.append('id', couponCode.id.toString());
    formData.append('title', couponCode.title);
    couponCode.description &&
      formData.append('description', couponCode.description);
    formData.append('discount_type', couponCode.discountType);
    formData.append('value', couponCode.value.toString());

    if (couponCode.recipient) {
      formData.append('recipient', couponCode.recipient);
    }

    if (couponCode.allowedSeminarIds && couponCode.allowedSeminarIds.length > 0) {
      couponCode.allowedSeminarIds.forEach(id => {
        formData.append('allowed_seminar_ids[]', id.toString());
      });
    }

    if (couponCode.seminarInverted) {
      formData.append('seminar_inverted', '1');
    }

    if (couponCode.allowedConditionTypes && couponCode.allowedConditionTypes.length > 0) {
      couponCode.allowedConditionTypes.forEach(type => {
        formData.append('allowed_condition_types[]', type);
      });
    }

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'coupon_codes.php', formData)
      .pipe(
        map(response => response),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  public validateCouponCode(code: string): Observable<any> {
    const formData = new FormData();
    formData.append('mode', 'checkCouponCodeValidity');
    formData.append('code', code);

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: CouponCodeBackendModel;
      }>(environment.authority_short + 'coupon_codes.php', formData)
      .pipe(
        map(response => this.parseCouponCodeBackendModel(response.data)),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  public createPayPalOrder(amount: number): Observable<any> {
    const formData = new FormData();
    formData.append('mode', 'createPayPalOrder');
    formData.append('amount', amount.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: any;
      }>(environment.authority_short + 'coupons.php', formData)
      .pipe(
        map(response => response.data),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  public buyCoupons(
    coupons: { id: number; amount: number; price: number }[],
    invoiceAddress: InvoiceAddressBackendModel,
    totalAmount: number
  ): Observable<any> {
    const formData = new FormData();
    formData.append('mode', 'buyCoupons');
    formData.append('coupons', JSON.stringify(coupons));
    formData.append('invoice_address', JSON.stringify(invoiceAddress));
    formData.append('total_amount', totalAmount.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'coupons.php', formData)
      .pipe(
        map(response => response),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  public generateCouponCodes(
    amount: number,
    title: string,
    value: number,
    expirationYear?: number,
    allowedSeminarIds?: number[],
    allowedConditionTypes?: string[]
  ): Observable<string[]> {
    const formData = new FormData();
    formData.append('mode', 'generateCouponCodes');
    formData.append('amount', amount.toString());
    formData.append('coupon_title', title);
    formData.append('coupon_value', value.toString());
    if (expirationYear) {
      formData.append('expiration_years', expirationYear.toString());
    }

    if (allowedSeminarIds && allowedSeminarIds.length > 0) {
      allowedSeminarIds.forEach(id => {
        formData.append('allowed_seminar_ids[]', id.toString());
      });
    }

    if (allowedConditionTypes && allowedConditionTypes.length > 0) {
      allowedConditionTypes.forEach(type => {
        formData.append('allowed_condition_types[]', type);
      });
    }

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: string[];
      }>(environment.authority_short + 'coupons.php', formData)
      .pipe(
        map(response => response.data),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  private parseBackendModel(backendModel: CouponBackendModel): CouponModel {
    return {
      id: backendModel.id,
      title: backendModel.title,
      description: backendModel.description,
      payedAmount: backendModel.payed_amount,
      receivedAmount: backendModel.received_amount,
      couponType: backendModel.coupon_type,
      isActive: Boolean(backendModel.is_active),
    };
  }

  public parseCouponCodeBackendModel(
    backendModel: CouponCodeBackendModel
  ): CouponCodeModel {
    return {
      id: backendModel.id,
      title: backendModel.title,
      description: backendModel.description,
      code: backendModel.code,
      discountType: backendModel.discount_type,
      expiresAt: backendModel.expires_at,
      isActive: Boolean(backendModel.is_active),
      maxUses: backendModel.max_uses,
      recipient: backendModel.recipient,
      type: backendModel.type,
      value: backendModel.value,
      allowedSeminarIds: this.normalizeAllowedSeminarIds(
        backendModel.allowed_seminar_ids
      ),
      allowedConditionTypes: backendModel.allowed_condition_types || [],
      seminarInverted: Boolean(backendModel.seminar_inverted),
    };
  }

  private normalizeAllowedSeminarIds(
    allowedSeminarIds?: number[] | Array<{ id: number; title: string }>
  ): number[] {
    if (!allowedSeminarIds || allowedSeminarIds.length === 0) {
      return [];
    }

    if (typeof allowedSeminarIds[0] === 'object') {
      return (allowedSeminarIds as Array<{ id: number; title: string }>).map(
        seminar => seminar.id
      );
    }

    return allowedSeminarIds as number[];
  }

  private parseCouponUsageLog(
    backendModel: CouponUsageLogBackendModel
  ): CouponUsageLogModel {
    const status = (() => {
      if (
        backendModel.expires_at &&
        new Date(backendModel.expires_at) < new Date()
      ) {
        return 'expired';
      }
      if (backendModel.redeemed_by && backendModel.redeemed_by.length > 0) {
        return 'used';
      }
      if (backendModel.is_deleted) {
        return 'deleted';
      }
      if (!backendModel.is_active) {
        return 'inactive';
      }
      if (backendModel.type === 'sellable') {
        return 'sold';
      }
      return 'active';
    })();

    const restrictions: {
      allowedSeminars?: Array<{
        id: number;
        title: string;
        categoryId?: number;
        dateSeriesId?: number;
      }>;
      allowedConditionTypes?: string[];
    } = {};

    if (
      backendModel.allowed_seminars &&
      backendModel.allowed_seminars.length > 0
    ) {
      restrictions.allowedSeminars = backendModel.allowed_seminars.map(
        seminar => ({
          id: seminar.id,
          title: seminar.title,
        })
      );
    }

    if (
      backendModel.allowed_condition_types &&
      backendModel.allowed_condition_types.length > 0
    ) {
      restrictions.allowedConditionTypes = backendModel.allowed_condition_types;
    }

    return {
      title: backendModel.title,
      redeemedBy: backendModel.redeemed_by,
      value: backendModel.value,
      discountType: backendModel.discount_type,
      type: backendModel.type,
      code: backendModel.code,
      status: status,
      isDeleted: backendModel.is_deleted,
      isActive: backendModel.is_active,
      expiresAt: backendModel.expires_at && new Date(backendModel.expires_at),
      restrictions:
        Object.keys(restrictions).length > 0 ? restrictions : undefined,
    };
  }
}
